import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { OrganizationStatus } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card';
import { Plus, RefreshCw } from 'lucide-react';
import { parseAsInteger, parseAsString, parseAsStringEnum, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { OrganizationsTable } from '@/components/tables/organizations-table';
import {
  type TOrganizationsTableFilters,
  ZOrganizationTableFiltersSchema,
} from '@/components/tables/organizations-table-filters';
import { trpc } from '@/providers/trpc';
import type { RouterOutputs } from '@/providers/trpc-client';

export const OrganizationsPage = () => {
  useLocale();

  const navigate = useNavigate();

  const [params, setParams] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      perPage: parseAsInteger.withDefault(10),
      search: parseAsString.withDefault(''),
      status: parseAsStringEnum<OrganizationStatus>(Object.values(OrganizationStatus)),
    },
    {
      history: 'replace',
      shallow: false,
    },
  );
  const filters: TOrganizationsTableFilters = useMemo(() => {
    try {
      return ZOrganizationTableFiltersSchema.parse(params);
    } catch {
      return ZOrganizationTableFiltersSchema.parse({});
    }
  }, [getLocale(), params]);

  const handleFilterChange = useCallback(
    (key: keyof TOrganizationsTableFilters, value: string | undefined) => {
      setParams({
        [key]: value === undefined || value === '' ? null : value,
      });
    },
    [setParams],
  );

  const handleClearFilters = useCallback(() => {
    setParams({ search: '' });
  }, [setParams]);

  const {
    data: organizationsData,
    isFetching,
    isRefetching,
    isLoading: isOrganizationsLoading,
    refetch,
    isError,
    error,
  } = trpc.admin.listOrganizations.useQuery({
    page: params.page,
    perPage: params.perPage,
    name: params.search || undefined,
    status: params.status || undefined,
  });
  const isLoading = isFetching || isOrganizationsLoading || isRefetching;

  const organizations: RouterOutputs['admin']['listOrganizations'] = organizationsData ?? {
    data: [],
    perPage: 10,
    page: 1,
    totalPages: 1,
    total: 0,
  };
  return (
    <div className="flex-1 space-y-6 p-4 pt-6 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-bold text-3xl tracking-tight">{t('workspace.organizations')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('auth.manageSystemOrganizationsAndTheirAccountStatus')}{' '}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('/admin/organizations/create')}>
            <Plus className="me-2 h-4 w-4" />
            {t('auth.addOrganization')}{' '}
          </Button>
          <Button disabled={isLoading} onClick={() => refetch()} variant="outline">
            <RefreshCw className={`me-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            {t('auth.refresh')}{' '}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('auth.allOrganizations')}</CardTitle>
          <CardDescription>
            {isLoading
              ? t('auth.loadingOrganizations')
              : t('count.organizations', { count: organizations?.data.length ?? 0 })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <OrganizationsTable
            data={organizations.data}
            error={error}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            onClearFilters={handleClearFilters}
            onFilterChange={handleFilterChange}
            onRefetch={refetch}
          />
        </CardContent>
      </Card>
    </div>
  );
};
